import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Save,
  RotateCcw,
} from 'lucide-react';
import { systemSettingsApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';

interface SettingGroup {
  category: string;
  settings: Array<{ key: string; value: unknown }>;
}

export const SystemSettingsPage = () => {
  const queryClient = useQueryClient();
  const [editedValues, setEditedValues] = useState<Record<string, unknown>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, unknown>>({});

  const { data: settingsRes, isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => systemSettingsApi.getAll(),
    staleTime: 30000,
  });

  const allSettings: Record<string, unknown> = settingsRes?.data?.data || {};

  useEffect(() => {
    if (Object.keys(allSettings).length > 0) {
      setOriginalValues(allSettings);
      setEditedValues(allSettings);
    }
  }, [settingsRes]);

  // Group settings by category prefix
  const groupedSettings: SettingGroup[] = (() => {
    const groups: Record<string, Array<{ key: string; value: unknown }>> = {};
    Object.entries(editedValues).forEach(([key, value]) => {
      const category = key.includes('.') ? key.split('.')[0] : 'general';
      if (!groups[category]) groups[category] = [];
      groups[category].push({ key, value });
    });
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, settings]) => ({ category, settings }));
  })();

  const dirtyKeys = Object.keys(editedValues).filter(
    (key) => JSON.stringify(editedValues[key]) !== JSON.stringify(originalValues[key])
  );

  const bulkUpdateMutation = useMutation({
    mutationFn: (settings: Record<string, unknown>) => systemSettingsApi.bulkUpdate(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success('All settings saved');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Bulk update failed'),
  });

  const handleChange = (key: string, value: unknown) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveAll = () => {
    const changed: Record<string, unknown> = {};
    dirtyKeys.forEach((key) => { changed[key] = editedValues[key]; });
    if (Object.keys(changed).length === 0) {
      toast.error('No changes to save');
      return;
    }
    bulkUpdateMutation.mutate(changed);
  };

  const handleReset = () => {
    setEditedValues({ ...originalValues });
  };

  const renderInput = (key: string, value: unknown) => {
    if (typeof value === 'boolean') {
      return (
        <button
          onClick={() => handleChange(key, !value)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            value ? 'bg-teal-500' : 'bg-white/[0.1]'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      );
    }
    if (typeof value === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => handleChange(key, Number(e.target.value))}
          className="input-dark w-48"
        />
      );
    }
    return (
      <input
        type="text"
        value={String(value ?? '')}
        onChange={(e) => handleChange(key, e.target.value)}
        className="input-dark w-64"
      />
    );
  };

  const formatLabel = (key: string) => {
    const parts = key.split('.');
    const label = parts[parts.length - 1];
    return label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">System Settings</h1>
          <p className="text-sm text-white/50 mt-1">Configure system-wide settings</p>
        </div>
        <div className="flex items-center space-x-3">
          {dirtyKeys.length > 0 && (
            <>
              <button onClick={handleReset} className="btn-outline flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </button>
              <button onClick={handleSaveAll} disabled={bulkUpdateMutation.isPending} className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
                <Save className="w-4 h-4" />
                <span>{bulkUpdateMutation.isPending ? 'Saving...' : `Save ${dirtyKeys.length} Change${dirtyKeys.length > 1 ? 's' : ''}`}</span>
              </button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center text-white/50 py-8">Loading settings...</div>
      ) : groupedSettings.length === 0 ? (
        <div className="card-dark p-8 text-center">
          <Settings className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/60">No settings configured</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedSettings.map((group) => (
            <div key={group.category} className="card-dark overflow-hidden">
              <div className="p-4 border-b border-white/[0.06] flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Settings className="w-4 h-4 text-teal-400" />
                </div>
                <h2 className="text-sm font-semibold text-white/90 capitalize">{group.category}</h2>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {group.settings.map(({ key, value }) => {
                  const isDirty = JSON.stringify(value) !== JSON.stringify(originalValues[key]);
                  return (
                    <div key={key} className={`flex items-center justify-between p-4 ${isDirty ? 'bg-teal-500/5' : ''}`}>
                      <div>
                        <p className="text-sm font-medium text-white/90">{formatLabel(key)}</p>
                        <p className="text-xs text-white/40 font-mono">{key}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        {renderInput(key, value)}
                        {isDirty && (
                          <span className="text-xs text-teal-400">Modified</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
